// Helpers e constants compartilhados entre os sub-componentes de Contas a Receber.
// Refactor topologico — não muda comportamento, apenas reorganiza.
import type { CSSProperties } from 'react';
import { fmtDataBr as _fmtDataBr } from '../../utils/dataFmt';
import type { Parcela, ColunaKey } from './types';

export const fmt = (v: any) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return 'R$ 0,00';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Postgres `numeric` retorna como string em node-pg. Operações de + concatenam
// em vez de somar. Sempre usar num() antes de reduce/sort/comparação numérica.
export const num = (v: any): number => {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? 0 : n;
};

export const fmtData = _fmtDataBr;

export const STATUS_PARC: Record<string, { cor: string; bg: string; label: string }> = {
  pendente:               { cor: '#888',    bg: '#F2F2F4', label: 'Pendente' },
  faturada:               { cor: '#B07500', bg: '#FFF8E8', label: 'Faturada' },
  parcialmente_recebida:  { cor: '#0930D0', bg: '#EFF3FF', label: 'Parcialmente recebida' },
  recebida:               { cor: '#0a7f4a', bg: '#E8F5EE', label: 'Recebida' },
  atrasada:               { cor: '#B91626', bg: '#FDECEC', label: 'Atrasada' },
  cancelada:              { cor: '#888',    bg: '#F2F2F4', label: 'Cancelada' },
};

export const TIPO_PARCELA_LABEL: Record<string, { label: string; cor: string; bg: string; icon: string }> = {
  mobilizacao: { label: 'Mobilização', cor: '#7C3AED', bg: '#F3E8FF', icon: '🚀' },
  medicao:     { label: 'Medição',     cor: '#0930D0', bg: '#EFF3FF', icon: '📐' },
  saldo:       { label: 'Saldo',       cor: '#0a7f4a', bg: '#E8F5EE', icon: '✓' },
  avulsa:      { label: 'Avulsa',      cor: '#888',    bg: '#F2F2F4', icon: '•' },
};

// F-CR-tipo: tipos de receita pra filtro Diretor (contrato) vs Marina (tudo).
// Diretor vê só receita operacional. Financeiro classifica o resto manualmente.
export const TIPO_RECEITA_LABEL: Record<string, { label: string; cor: string; bg: string; icon: string }> = {
  contrato:                    { label: 'Contrato',                  cor: '#0930D0', bg: '#EFF3FF', icon: '📄' },
  receita_financeira:          { label: 'Receita financeira',        cor: '#7C3AED', bg: '#F3E8FF', icon: '📈' },
  aporte_capital:              { label: 'Aporte capital',            cor: '#0a7f4a', bg: '#E8F5EE', icon: '💼' },
  outras_receitas:             { label: 'Outras receitas',           cor: '#B07500', bg: '#FFF8E8', icon: '➕' },
  transferencia_entre_contas:  { label: 'Transferência entre contas', cor: '#5B7CFF', bg: '#EFF3FF', icon: '🔄' },
  // Devolução de empréstimo a sócio: movimento patrimonial (reduz o direito a
  // receber), nunca receita e nunca tributado.
  mutuo_socio:                 { label: 'Mútuo com sócio',           cor: '#0a7f4a', bg: '#E8F5EE', icon: '🤝' },
  // Principal de empréstimo bancário que ENTROU: aumenta o passivo, nunca é
  // receita e nunca é tributado. Só o juro da prestação é despesa, e ele vem do
  // módulo de Empréstimos.
  emprestimo_captado:          { label: 'Empréstimo captado',        cor: '#0a7f4a', bg: '#E8F5EE', icon: '🏦' },
  nao_classificada:            { label: 'Não classificada',          cor: '#B91626', bg: '#FDECEC', icon: '⚠️' },
};

export const TIPO_RECEITA_OPTIONS = Object.entries(TIPO_RECEITA_LABEL).map(([value, v]) => ({
  value, label: `${v.icon} ${v.label}`,
}));

// Toggle persistente por usuário do filtro padrão de tipo. Localstorage.
export const LS_KEY_CR_TIPO_DEFAULT = 'fin_cr_tipo_default_v1';

export const PROBAB_COR: Record<string, { cor: string; bg: string }> = {
  'Confirmado':  { cor: '#0a7f4a', bg: '#E8F5EE' },
  'Alta':        { cor: '#0930D0', bg: '#EFF3FF' },
  'Média':       { cor: '#B07500', bg: '#FFF8E8' },
  'Baixa':       { cor: '#888',    bg: '#F2F2F4' },
};

// Sprint F5 — setores de negócio (espelha comercial.setores_negocio no DB).
// Quando rodar F8/futuras alterações, podemos carregar via API. Por ora,
// hardcoded pra evitar request extra e manter consistência de cor/ordem.
// 04/10/2026 - nomes oficiais das 6 areas (decisao do Lucas): Projetos e Supervisao e
// Fiscalizacao de Projetos. Sao os mesmos de comercial.setores_negocio.rotulo e do catalogo
// de servicos (utils/areasEmpresa.ts reexporta esta lista); o teste
// comercial-engine/tests/catalogo/concordancia.test.js trava os tres iguais.
export const SETORES: Record<string, { rotulo: string; cor: string; ordem: number }> = {
  topografia:            { rotulo: 'Topografia',              cor: '#0930D0', ordem: 1 },
  hidrometria:           { rotulo: 'Hidrometria',             cor: '#02B5DB', ordem: 2 },
  projeto:               { rotulo: 'Projetos',                cor: '#020D7F', ordem: 3 },
  consultoria_ambiental: { rotulo: 'Consultoria Ambiental',   cor: '#0a7f4a', ordem: 4 },
  fiscalizacao_projeto:  { rotulo: 'Supervisão e Fiscalização de Projetos', cor: '#B07500', ordem: 5 },
  seguranca_barragem:    { rotulo: 'Segurança de Barragem',   cor: '#B91626', ordem: 6 },
};

// Sprint F8 — Definição das colunas da tabela (reordenáveis).
// Cada coluna tem chave, label, estilo do th, e função `render` que devolve
// o conteúdo do <td> dado uma Parcela. A ordem efetiva vem do estado
// `ordemColunas` (persistido em localStorage).
// Sprint F8.3 — Coluna "Emissão" substitui "Vencimento" no default. Quem
// já tinha "vencimento" salvo no localStorage continua vendo aquela coluna.
// 'origem' (auditoria) adicionada no default — bump da LS key força re-aplicar.
export const COLUNAS_DEFAULT: ColunaKey[] = ['contrato', 'cliente', 'parcela', 'emissao', 'previsto', 'bm', 'status', 'nf', 'origem', 'acao'];
export const LS_KEY_ORDEM_COLUNAS = 'fin_cr_ordem_colunas_v2';
export const LS_KEY_ORDENACAO = 'fin_cr_ordenacao_v1';

// Sprint F8.1 — Quais colunas são sortáveis e como extrair o valor de cada
// parcela pra comparação. BM e Ação não fazem sentido ordenar.
export const COLUNAS_SORTAVEIS: ColunaKey[] = ['contrato', 'cliente', 'parcela', 'emissao', 'vencimento', 'previsto', 'status', 'nf', 'origem'];

// Ordem semântica dos status (atrasada primeiro, cancelada por último)
export const ORDEM_STATUS: Record<string, number> = {
  atrasada: 0, pendente: 1, faturada: 2, parcialmente_recebida: 3, recebida: 4, cancelada: 5,
};

export function valorSortColuna(p: Parcela, coluna: ColunaKey): string | number {
  switch (coluna) {
    case 'contrato': {
      // Ordena por contrato_numero, depois pelo número da parcela (com padding)
      const numStr = String(p.numero_parcela ?? 999).padStart(6, '0');
      return `${(p.contrato_numero || '').toLowerCase()}|${numStr}`;
    }
    case 'cliente':    return (p.cliente_nome || '').toLowerCase();
    case 'parcela':    return (p.parcela_titulo || p.descricao || '').toLowerCase();
    case 'emissao':    return (p.nota_fiscal_emissao || '9999-12-31').slice(0, 10);
    case 'vencimento': return (p.data_vencimento || '9999-12-31').slice(0, 10);
    case 'previsto':   return Number(p.valor_previsto) || 0;
    case 'status':     return ORDEM_STATUS[p.status] ?? 99;
    case 'nf':         return (p.nota_fiscal_numero || '').toLowerCase();
    case 'origem':     return (p.origem || 'zzz').toLowerCase();
    default:           return '';
  }
}

export const inputMP: CSSProperties = {
  width: '100%', padding: '8px 12px', fontSize: 13, border: '0.5px solid #ddd',
  borderRadius: 4, boxSizing: 'border-box', background: '#fff',
};
